import { useTranslation } from "react-i18next";

import WPTertiaryButton from "../../../../packages/WPTertiaryButton";
import IconButton from "../../../../packages/IconButton";
import CircularLoader from "../../../../common/styles/components/circularLoader";
import CustomModal from "../../../../packages/CustomModal";
import WPMessageBanner, {
  BANNER_TYPES,
} from "../../../../packages/WPMessageBanner/WPMessageBanner";
import {
  ExcelIcon,
  FileIcon,
  RemoveIcon,
  SuccessIcon,
} from "../../../../assets/icons";
import { getFileExtension } from "../../../../utils/appUtils";
import {
  FILE_UPLOAD,
  STATUS_KEYS,
} from "../../../../constants/global.constants";

const getFileSizeText = (sizeInBytes) => {
  if (!sizeInBytes) return "0 kb";
  return `${Math.max(1, Math.round(sizeInBytes / 1024))} kb`;
};

const DatasetUploadModal = ({
  selectedUploadFiles,
  totalTrainCount,
  onModalClose,
  uploadedAndSelectedFilesCount,
  onAddMoreFiles,
  onReplace,
  onRemove,
  hideAddMore,
}) => {
  const { t } = useTranslation();

  const areSelectedFilesUploading = selectedUploadFiles.some(
    (uploadFileItem) => uploadFileItem.status === STATUS_KEYS.LOADING,
  );
  const hasErrorInSelectedFiles = selectedUploadFiles.some(
    (uploadFileItem) => uploadFileItem.error,
  );
  const areAllSelectedFilesSuccessful = selectedUploadFiles.every(
    (uploadFileItem) => uploadFileItem.status === STATUS_KEYS.SUCCESS,
  );

  const areMoreFilesSelectedThanLimit =
    uploadedAndSelectedFilesCount > totalTrainCount;

  const isFileSelectionLimitReached =
    uploadedAndSelectedFilesCount >= totalTrainCount;

  const handleAddMoreFileClick = () => {
    if (onAddMoreFiles) {
      onAddMoreFiles();
    }
  };

  const uploadLimitExceededHeader = t(
    "analyseData.maxUploadLimitExceededTitle",
    {
      maxFiles: totalTrainCount,
    },
  );
  const uploadLimitExceededBody = t("analyseData.maxUploadLimitExceededBody", {
    maxFiles: totalTrainCount,
  });

  const addMoreFileFooter = (
    <WPTertiaryButton
      id="add-more-files-btn"
      onClick={handleAddMoreFileClick}
      disabled={areSelectedFilesUploading || isFileSelectionLimitReached}
      tooltip={
        isFileSelectionLimitReached
          ? t("analyseData.maxUploadLimitReached")
          : ""
      }
      className="upload-file-modal__add-more-btn"
      label={t("analyseData.addMoreFiles")}
      size="small"
    />
  );

  return (
    <CustomModal
      header={t("analyseData.uploadFiles")}
      headerHelper={t("analyseData.uploadModalSubText")}
      onModalClose={onModalClose}
      confirmBtn={
        areAllSelectedFilesSuccessful ? null : t("analyseData.uploadFiles")
      }
      cancelBtn={
        areAllSelectedFilesSuccessful
          ? t("ValueStrings.done")
          : t("ValueStrings.cancel")
      }
      confirmBtnDisabled={
        !selectedUploadFiles.length ||
        areSelectedFilesUploading ||
        hasErrorInSelectedFiles ||
        areMoreFilesSelectedThanLimit
      }
      cancelBtnDisabled={areSelectedFilesUploading}
      hideCloseIcon
      width="660px"
      customclass="dataset-upload-file-modal"
      custombodyclass="upload-file-modal__content"
      modalFooter={hideAddMore ? null : addMoreFileFooter}
      confirmBtnTooltip={
        areMoreFilesSelectedThanLimit || hasErrorInSelectedFiles
          ? t("analyseData.resolveErrorsToProceed")
          : ""
      }
    >
      {areMoreFilesSelectedThanLimit && (
        <WPMessageBanner
          type={BANNER_TYPES.error}
          header={uploadLimitExceededHeader}
          message={uploadLimitExceededBody}
          className="upload-file-modal__limit-banner"
        />
      )}

      {selectedUploadFiles.map((uploadQueueItem, index) => {
        const uploadFile = uploadQueueItem?.file;
        const displayName = uploadQueueItem?.resolvedName || uploadFile?.name;
        const hasError = Boolean(uploadQueueItem?.error);
        const isUploadingFile = uploadQueueItem?.status === STATUS_KEYS.LOADING;
        const isUploadSuccess = uploadQueueItem?.status === STATUS_KEYS.SUCCESS;
        const fileExtension = getFileExtension(uploadFile?.name || "");

        return (
          <div className="upload-file-row" key={`${displayName}-${index}`}>
            <div className="upload-file-row__meta">
              {fileExtension === "csv" ||
              !FILE_UPLOAD.SUPPORTED_FILE_TYPES.includes(
                fileExtension.toUpperCase(),
              ) ? (
                <FileIcon className="upload-file-row__icon" />
              ) : (
                <ExcelIcon className="upload-file-row__icon" />
              )}
              <div className="upload-file-row__text">
                <div className="upload-file-row__name">{displayName}</div>
                <div
                  className={`upload-file-row__size ${
                    hasError ? "upload-file-row__size--error" : ""
                  }`}
                >
                  {hasError
                    ? uploadQueueItem.error
                    : getFileSizeText(uploadFile?.size)}
                </div>
              </div>
            </div>
            {isUploadingFile ? (
              <CircularLoader size="20px" className="upload-file-row__loader" />
            ) : isUploadSuccess ? (
              <SuccessIcon className="upload-file-row__success-icon" />
            ) : (
              <div className="upload-file-row__actions">
                {hasError && (
                  <WPTertiaryButton
                    id={`replace-upload-${index}`}
                    className="upload-file-row__replace"
                    onClick={() => onReplace(index)}
                    label={t("fileUpload.replace")}
                    variant="outlined"
                    size="small"
                  />
                )}
                <IconButton
                  id={`remove-upload-${index}`}
                  className="upload-file-row__remove"
                  onClick={() => onRemove(index)}
                  tooltip={t("ValueStrings.remove")}
                >
                  <RemoveIcon className="upload-file-row__remove-icon" />
                </IconButton>
              </div>
            )}
          </div>
        );
      })}
    </CustomModal>
  );
};

export default DatasetUploadModal;
